/**
 * invoice-check.js
 *
 * Handles the "RASTI KVITĄ" receipt lookup:
 *  - Validates all four fields are filled.
 *  - Calls the WP REST proxy → 3rd-party API.
 *  - Renders a receipt result row in .ig-receipts-list.
 *  - Enables the slide-2 submit button when at least one receipt row exists.
 *  - PAŠALINTI removes a row; disables submit when the list is empty again.
 *
 * NOTE: Date input validation is intentionally left as a TODO stub.
 */

/**
 * Safely escape a string for insertion into innerHTML.
 * @param {string} str
 * @returns {string}
 */
function escHtml(str) {
	const div = document.createElement('div');
	div.textContent = String(str ?? '');
	return div.innerHTML;
}

import { initDatepicker } from './datepicker';
import { downloadPdfFromBase64 } from './utils';

/**
 * @param {HTMLFormElement}        form
 * @param {import('swiper').default|null} swiper
 * @param {import('./api').default}       api
 * @param {import('./modal-system').default} modal
 */
export function initReceiptCheck(form, swiper, api, modal) {
	const checkBtn = form.querySelector('.ig-check-btn');
	const submitBtn = form.querySelector('.ig-submit-btn');
	const listEl = form.querySelector('.ig-receipts-list');
	const statusMsg = form.querySelector('.ig-status-msg');

	const receiptNumberEl = form.querySelector('#ig-receipt-number');
	const registerNumberEl = form.querySelector('#ig-register-number');
	const dateEl = form.querySelector('#ig-receipt-date');
	const amountEl = form.querySelector('#ig-receipt-amount');

	// Initialize datepicker
	if (dateEl) {
		initDatepicker(dateEl, { classes: 'cancan-datepicker' });
	}

	if (amountEl) {
		amountEl.oninput = () => {
			amountEl.value = amountEl.value
				.replace(/,/g, '.') // comma → period
				.replace(/[^0-9.]/g, '') // allow only numbers + dot
				.replace(/(\..*)\./g, '$1'); // only one dot
		};
	}

	function bindReceiptSVGAreas() {
		const receipt = form.querySelector('.ig-receipt__wrapper svg');
		if (!receipt) return;

		const hCheckNumber = receipt.querySelector('rect.check-number');
		const hDate = receipt.querySelector('rect.date');
		const hTerminalId = receipt.querySelector('rect.terminal-id');
		const hPrice = receipt.querySelector('rect.price');

		const bind = (el, highlight) => {
			if (!el || !highlight) return;
			el.addEventListener('focus', () =>
				highlight.classList.add('active')
			);
			el.addEventListener('blur', () =>
				highlight.classList.remove('active')
			);
		};

		bind(receiptNumberEl, hCheckNumber);
		bind(dateEl, hDate);
		bind(registerNumberEl, hTerminalId);
		bind(amountEl, hPrice);
	}

	if (!checkBtn) return;

	/** Receipts added so far this session. */
	let receipts = [];

	// ── Helpers ──────────────────────────────────────────────────────────────

	const t = (key, fallback) => window.igTranslations?.[key] ?? fallback;

	const toggleLoader = (show) => {
		const loader = form.querySelector('.ig-loader');
		loader?.classList.toggle('active', show);
		if (checkBtn) checkBtn.disabled = show;
	};

	const setStatus = (msg, isError = false) => {
		if (!statusMsg) return;
		statusMsg.textContent = msg;
		statusMsg.classList.toggle('ig-status-msg--error', isError);
	};

	const updateSubmitBtn = () => {
		if (submitBtn) submitBtn.disabled = receipts.length === 0;
	};

	const resetReceiptState = () => {
		receipts = [];
		listEl?.classList.remove('is-active');
		updateSubmitBtn();
	};

	// TODO: Implement already existing receipt
	const isReceiptAlreadyAdded = (data) => {
		console.log('Checking if receipt already added');
		return receipts.some(
			({ data: receipt }) =>
				receipt.ReceiptNumber === data.receiptNumber &&
				receipt.RegisterNumber === data.registerNumber &&
				receipt.Date === data.date &&
				receipt.Amount === data.amount
		);
	};

	// ── Render receipt row ────────────────────────────────────────────────────
	// Note: We now use a static row present in the DOM for consistency.

	const renderReceiptRow = (receipt) => {
		const row = listEl.querySelector('.ig-receipt-row');
		if (!row) return;

		const data = receipt.data;

		// Populate group label
		const groupLabelEl = listEl.querySelector('.ig-receipts-group-label');
		if (groupLabelEl) {
			groupLabelEl.textContent = data.GroupLabel ?? data.Location ?? '';
		}

		// Populate fields
		const fields = {
			'.ig-row-register':
				data.RegisterNumber ?? registerNumberEl?.value ?? '',
			'.ig-row-receipt':
				data.ReceiptNumber ?? receiptNumberEl?.value ?? '',
			'.ig-row-date': data.Date ?? dateEl?.value ?? '',
			'.ig-row-amount': data.Amount ?? amountEl?.value ?? '',
		};

		Object.entries(fields).forEach(([selector, value]) => {
			const el = row.querySelector(selector);
			if (el) el.textContent = value;
		});

		// Update modal trigger data
		const viewBtn = row.querySelector('.ig-view-btn');
		if (viewBtn) {
			// Clear old listeners if any (though since we allow only one, this is simple)
			const newViewBtn = viewBtn.cloneNode(true);
			viewBtn.parentNode.replaceChild(newViewBtn, viewBtn);
			newViewBtn.addEventListener('click', () => {
				modal.open('tmpl-ig-receipt-preview', data);
			});
		}

		// Update remove button logic
		const removeBtn = row.querySelector('.ig-remove-btn');
		if (removeBtn) {
			const newRemoveBtn = removeBtn.cloneNode(true);
			removeBtn.parentNode.replaceChild(newRemoveBtn, removeBtn);
			newRemoveBtn.addEventListener('click', () => {
				resetReceiptState();
			});
		}

		listEl.classList.add('is-active');
	};

	// ── Main check handler ────────────────────────────────────────────────────

	const handleCheck = async () => {
		const receiptNumber = receiptNumberEl?.value.trim() ?? '';
		const registerNumber = registerNumberEl?.value.trim() ?? '';
		const date = dateEl?.value.trim() ?? '';
		const amount = amountEl?.value.trim() ?? '';

		// Basic "all fields filled" guard (date validation intentionally left as TODO)
		if (!receiptNumber || !registerNumber || !date || !amount) {
			setStatus(
				t('error_fields_required', 'Užpildykite visus laukus.'),
				true
			);
			return;
		} else if (
			isReceiptAlreadyAdded({
				receiptNumber,
				registerNumber,
				date,
				amount,
			})
		) {
			setStatus(
				t('error_receipt_already_added', 'Kvitas jau pridėtas.'),
				true
			);
			return;
		}

		setStatus('');

		try {
			toggleLoader(true);

			const response = await api.checkReceipt({
				receiptNumber,
				registerNumber,
				date,
				amount,
			});

			const body = response.body ?? response;
			const d = body?.data;

			// Map data if receipt found (Status 0 = new, Status 1 = already invoiced)
			if ((body?.status === 0 || body?.status === 1) && d) {
				const seller = d.SELLER || {};

				// Map to our unified format for the UI & Modal
				const receiptData = {
					data: {
						GroupLabel: seller.location_name
							? `${seller.location_name}, ${seller.company_name}`
							: '',
						Location: seller.location_name || '',
						Address: seller.location_address || '',
						VatCode: seller.company_vatcode || '',
						BusinessCode: seller.company_code || '',
						PaymentMethod: d.PAYMENTS?.[0]?.PAYMENT_NAME || '',
						Total: d.TOTAL || amount,
						Date: d.PBILL_DATETIME
							? d.PBILL_DATETIME.substring(0, 10).replace(
									/\//g,
									'-'
								)
							: date,
						RegisterNumber: d.FISCAL_ID || registerNumber,
						ReceiptNumber: d.FISC_DOC || receiptNumber,
						Items: (d.ITEMS || []).map((item) => ({
							Name:
								item.ARTICLE_NAME ||
								item.ARTICLE_WITH_SIZEM ||
								'',
							Quantity: Number(item.QTY || 1).toFixed(2),
							Sum: Number(item.PRICE || 0).toFixed(2),
							VatRate: (item.VAT_PERCENT || 0) + '%',
						})),
						Amount: amount, // Save the input amount for isReceiptAlreadyAdded check
					},
					raw: d,
					status: body.status,
				};

				// Render row and update receipts if receipt does not have an invoice
				if (body.status === 0) {
					receipts[0] = receiptData;
					renderReceiptRow(receiptData);
					updateSubmitBtn();
				} else if (body.status === 1) {
					// Special handling for Status 1: Trigger success modal and download immediately
					resetReceiptState();
					try {
						const pdfResponse = await api.generateInvoicePdf(d);
						if (pdfResponse.status === 0 && pdfResponse.data) {
							modal.open(
								'tmpl-ig-invoice-success',
								pdfResponse.data || {}
							);
							downloadPdfFromBase64(pdfResponse.data);
						}
					} catch (pdfErr) {
						console.error('[Invoice Check] PDF Error:', pdfErr);
					}
				}

				setStatus('');

				return;
			}

			// Error cases
			setStatus(
				body?.message ||
					t('error_receipt_not_found', 'Kvitas nerastas.'),
				true
			);

			updateSubmitBtn();
		} catch (err) {
			console.error('[Invoice Check] Error:', err);
			setStatus(
				t('error_generic', 'Įvyko klaida. Bandykite vėliau.'),
				true
			);
		} finally {
			toggleLoader(false);
		}
	};

	// ── Events ───────────────────────────────────────────────────────────────

	checkBtn.addEventListener('click', handleCheck);

	// Allow Enter key in any field to trigger the check
	[receiptNumberEl, registerNumberEl, dateEl, amountEl].forEach((el) => {
		el?.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				handleCheck();
			}
		});
	});

	// Submit → advance to slide 2
	if (submitBtn && swiper) {
		submitBtn.addEventListener('click', () => {
			console.log('Sliding');
			console.log(receipts);
			if (receipts.length > 0) {
				swiper.slideNext();
			}
		});
	}

	// Initialize SVG highlighting
	bindReceiptSVGAreas();

	return {
		getReceipts: () => receipts,
		reset: () => {
			if (receiptNumberEl) receiptNumberEl.value = '';
			if (registerNumberEl) registerNumberEl.value = '';
			if (dateEl) dateEl.value = '';
			if (amountEl) amountEl.value = '';

			resetReceiptState();
			setStatus('');
		},
	};
}
